import { useCallback, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import { useTheme } from "@mui/material";
import { toPercent } from "@helpers";
import type { ClassificationBreakdownData } from "@components";

export const useDonutChartOptions = (
  data: ClassificationBreakdownData[],
  chartHeight: number,
  onSectionClick: (item: ClassificationBreakdownData) => void,
  onSectionHover: (item: ClassificationBreakdownData | null) => void
) => {
  const theme = useTheme();
  const dataRef = useRef(data);
  dataRef.current = data;

  const handleClick = useCallback(
    (point: Highcharts.Point) => {
      const item = dataRef.current[point.index];
      if (item) {
        onSectionClick(item);
      }
    },
    [onSectionClick]
  );

  const handleMouseOver = useCallback(
    (point: Highcharts.Point) => {
      onSectionHover(dataRef.current[point.index] ?? null);
    },
    [onSectionHover]
  );

  const handleMouseOut = useCallback(() => {
    onSectionHover(null);
  }, [onSectionHover]);

  return useMemo<Highcharts.Options>(
    () => ({
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        height: chartHeight,
        animation: { duration: 600 },
      },
      title: { text: undefined },
      credits: { enabled: false },
      tooltip: {
        useHTML: true,
        backgroundColor: "rgba(27, 31, 34, 0.9)",
        borderWidth: 0,
        borderRadius: 8,
        padding: 8,
        shadow: false,
        style: {
          color: "#fff",
          fontSize: "12px",
          fontWeight: "500",
          lineHeight: "18px",
          fontFamily: theme.typography.fontFamily as string,
        },
        formatter: function () {
          const color = this.point.color as string;
          const pct = toPercent(
            (this.point.percentage ?? 0) / 100,
            "auto",
            2,
            2
          );
          return (
            `<span style="display:inline-flex;align-items:center;gap:6px;">` +
            `<span style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></span>` +
            `${pct}</span>`
          );
        },
      },
      plotOptions: {
        pie: {
          innerSize: "63%",
          borderWidth: 1,
          borderColor: theme.palette.background.paper,
          borderRadius: 0,
          allowPointSelect: false,
          dataLabels: { enabled: false },
          states: {
            hover: {
              brightness: 0.1,
            },
          },
          point: {
            events: {
              click: function () {
                handleClick(this);
              },
              mouseOver: function () {
                handleMouseOver(this);
              },
              mouseOut: function () {
                handleMouseOut();
              },
            },
          },
          startAngle: 0,
          cursor: "pointer",
        },
      },
      series: [
        {
          type: "pie" as const,
          data: data.map((entry) => ({
            name: entry.name,
            y: entry.value,
            color: entry.fillColor,
            cursor: entry.hasChildren ? "pointer" : "default",
          })),
        },
      ],
    }),
    [data, chartHeight, theme, handleClick, handleMouseOver, handleMouseOut]
  );
};
